import classNames from 'classnames';
import { activeChatbotIdSelector } from '@mm/state/selectors/viewSelectors';
import React from 'react';
import {
  SendOutlined,
  MoreOutlined,
  DeleteOutlined,
  CopyOutlined,
} from '@ant-design/icons';
import { Button } from '@mm/components';
import { StoreState } from '@mm/state/store-interfaces';
import { selectRecordsByUiFilterKind } from '@mm/state/selectors/campaignsOverviewSelectors';
import { useDispatch, useSelector } from 'react-redux';
import useRouter from 'use-react-router';
import modalActions from '@mm/state/modules/modalsState/actions';
import css from './AutomationRow.module.css';
import { useAutomationStatus } from '../../../../Automations/use-automation-status';
import { BotBuilderRoutes } from '../../../../../../../../routes/bot-builder-routes';
import {
  AutomationState,
  useAutomation,
} from '../../../../Automations/AutomationEdit/use-automation';
import { RowComponent } from '../../CampaignsTable';
import { ReviewBadge } from '../../../../Automations/AutomationEdit/DialogueEditor/NodeTypes/elements/ReviewBadge/ReviewBadge';
import { Menu } from '../../../../Automations/AutomationEdit/DialogueEditor/NodeTypes/elements/NodeMenu/NodeMenu';
import { useResolutionInfo } from '@mm/hooks';
import { useCampaignsOverviewCRUDActions } from '@mm/state/actions/CampaignsOverviewCRUDActions';
import { ReactComponent as OpenedEmailIcon } from 'assets/opened-email.svg';
import { ReactComponent as ClickedIcon } from 'assets/click.svg';
import { ConfigProvider, Switch } from 'antd';
import { useWhitelabelConfig } from '@mm/hooks/src/use-whitelabel-config';

export const AutomationRow: RowComponent = ({
  item,
  fetchCurrentActiveKind,
}) => {
  const { id, name } = item;
  const { screenWidth } = useResolutionInfo();
  const isMobile = screenWidth <= 700;

  const { history } = useRouter();
  const chatbotId = useSelector(activeChatbotIdSelector);
  const { pagination, automationStats: stats } =
    useSelector((state: StoreState) =>
      selectRecordsByUiFilterKind(state, 'automation'),
    ) || {};

  const automationStats = stats?.find(stat => stat?.[id])?.[id];

  const automation = useAutomation({
    id: item.id,
    initialState: {
      source: item,
      automation: item,
      busy: '',
      error: '',
    } as AutomationState,
  });

  const automationStatus = useAutomationStatus();
  const campaignsOverviewCRUDActions = useCampaignsOverviewCRUDActions();
  const status = automationStatus.get(item);

  const { isWhitelabelCompany, primaryColor } = useWhitelabelConfig();

  const dispatch = useDispatch();
  const onCloneAutomationClick = () => {
    dispatch(
      modalActions.openCloneAutomationModal({
        campaignId: automation.state.source?.id,
        retrieveCampaigns: true,
      }),
    );
  };
  const isAutomaticallyCreatedCampaign = Boolean(
    automation.state.source?.system,
  );

  const fetchCampaigns = async () => {
    await campaignsOverviewCRUDActions.retrieveAll({
      botId: chatbotId,
      kind: 'automation',
      pagination: {
        page: pagination?.page || 1,
        perPage: pagination?.perPage || 8,
      },
    });
  };

  const onDelete = () => {
    automation.actions.deleteAutomation({
      skipPush: true,
      onSuccess: fetchCampaigns,
    });
  };

  const menu = [
    {
      disabled: false,
      children: (
        <div className={css.menuItem}>
          {' '}
          <CopyOutlined className={css.menuItemIcon} /> Clone
        </div>
      ),
      onClick: onCloneAutomationClick,
    },
    {
      disabled: isAutomaticallyCreatedCampaign,
      children: (
        <div className={css.menuItem}>
          {' '}
          <DeleteOutlined className={css.menuItemIcon} /> Delete
        </div>
      ),
      onClick: onDelete,
    },
  ];

  const navigateToEditRoute = () => {
    history.push(
      BotBuilderRoutes.automationEdit.getLink({
        chatbotId,
        id,
      }),
    );
  };

  const onChange = async () => {
    if (!status.active) {
      await automation.actions.start();
    }

    if (status.active && !status.paused) {
      await automation.actions.pause();
    }

    if (status.active && status.paused) {
      await automation.actions.start();
    }
  };

  const statusFormatted = automationStatus.format(item);
  const inactive = statusFormatted !== 'Active';

  return (
    <div
      className={classNames(css.container, {
        [css.inactive]: inactive,
        [css.containerWithReview]: item.needsReview,
      })}
    >
      {item.needsReview && (
        <ReviewBadge
          onClick={navigateToEditRoute}
          className={css.reviewBadge}
          withIcon
          text="Needs review"
        />
      )}

      <div className={css.info}>
        <span className={css.title} onClick={navigateToEditRoute}>
          <abbr title={name}>{name}</abbr>
        </span>
        {!isMobile && (
          <Button css={{ container: css.button }} onClick={navigateToEditRoute}>
            Go to automation
          </Button>
        )}
      </div>

      <div className={css.automationStats}>
        <div
          className={classNames(css.automationStatItem, {
            [css.inactive]: inactive,
          })}
        >
          <SendOutlined className={css.automationStatIcon} />
          <div className={css.column}>
            <span className={css.number}>{automationStats?.sent || 0}</span>
            <span className={css.label}>Sent total</span>
          </div>
        </div>
        <div
          className={classNames(css.automationStatItem, {
            [css.inactive]: inactive,
          })}
        >
          <OpenedEmailIcon className={css.automationStatIcon} />
          <div className={css.column}>
            <span className={css.number}>{automationStats?.open || 0}</span>
            <span className={css.label}>Opened</span>
          </div>
        </div>
        <div
          className={classNames(css.automationStatItem, {
            [css.inactive]: inactive,
          })}
        >
          <ClickedIcon className={css.automationStatIcon} />
          <div className={css.column}>
            <span className={css.number}>{automationStats?.click || 0}</span>
            <span className={css.label}>Clicked</span>
          </div>
        </div>
      </div>

      <div className={css.actions}>
        <ConfigProvider
          theme={{
            components: {
              Switch: {
                colorPrimary: isWhitelabelCompany
                  ? primaryColor
                  : 'var(--color-geekblue-6)',
              },
            },
          }}
        >
          <Switch
            checkedChildren="active"
            unCheckedChildren="draft"
            className={css.switch}
            checked={!inactive}
            onChange={onChange}
          />
        </ConfigProvider>
        {isMobile && (
          <Button
            css={{ container: isMobile ? css.mobileButton : css.button }}
            onClick={navigateToEditRoute}
          >
            Go to automation
          </Button>
        )}

        {menu?.length && (
          <Menu
            className={css.menu}
            menu={menu}
            direction="bottom"
            menuButton={
              <Button css={{ container: css.menuButton }} color="secondary">
                <MoreOutlined className={css.moreIcon} />
              </Button>
            }
            css={{ menu: css.nodeMenu, action: css.actionButton }}
          />
        )}
      </div>
    </div>
  );
};
